import { useEffect } from "react";
import { useFormikContext } from "formik";
import { useTranslations } from "use-intl";
import { HireUnitType } from "@/gql/graphql";
import { RelocationFormValues } from "@/app/Relocations/Utils/getDefaultRelocationValues";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { supplierInsuranceOptionListQuery } from "@/app/Suppliers/GraphQL/supplierInsuranceOptionListQuery";

export function hasRouteAndVehicle(values: RelocationFormValues) {
  return Boolean(
    values.vehicle?.connect &&
      values.deliveryOfficeIds?.[0] &&
      values.departureOffice?.connect,
  );
}

export function useSupplierFormDefaults() {
  const { values, initialValues, setFieldValue, setValues } =
    useFormikContext<RelocationFormValues>();

  if (!values.supplier.connect) {
    throw new Error("A supplier is required");
  }

  const {
    data: { supplier },
  } = useSuspenseGqlQuery(supplierRecordQuery, {
    id: values.supplier.connect,
  });

  const { data: insuranceOptionsData } = useSuspenseGqlQuery(
    supplierInsuranceOptionListQuery,
    {
      supplierId: values.supplier.connect,
      page: 1,
      first: 20,
    },
  );

  const defaultInsuranceOption =
    insuranceOptionsData.supplierInsuranceOptions.data.find(
      (opt) => opt.is_default,
    );

  useEffect(() => {
    if (supplier.id === initialValues.supplier?.connect) return;

    const updates: Partial<RelocationFormValues> = {
      booking_fee_amount: supplier.booking_fee_amount,
      charge_per_move_amount: supplier.charge_per_move_amount,
      holding_deposit_amount: supplier.is_holding_deposit_required
        ? supplier.holding_deposit_amount
        : 0,
      holding_deposit_refunded_by: supplier.holding_deposit_refunded_by,
      holding_deposit_refunded_at: supplier.holding_deposit_refunded_at,
      hire_unit_type: supplier.hire_unit_type,
      currency: supplier.currency,
      measurement: supplier.measurement,
      extra_distance_allowed_per_extra_hire_unit:
        supplier.extra_distance_allowed_per_extra_hire_unit,
      excess_distance_rate: supplier.excess_distance_rate,
      lines: {
        upsert: [],
        delete: [],
      },
      supplierInsuranceOption: {
        connect: defaultInsuranceOption?.id,
      },
    };

    setValues((old) => ({ ...old, ...updates }));
  }, [supplier.id]);

  useEffect(() => {
    if (values.supplierInsuranceOption.connect || !defaultInsuranceOption) {
      return;
    }

    setFieldValue("supplierInsuranceOption.connect", defaultInsuranceOption.id);
  }, [supplier.id, defaultInsuranceOption?.id]);

  return { supplier, defaultInsuranceOption };
}

export function useHireUnitLabels() {
  const t = useTranslations("relocation");
  const { values } = useFormikContext<RelocationFormValues>();

  const unitLabel = t(
    `hire_unit_type.${values?.hire_unit_type ?? HireUnitType.Day}`,
  ) as string;

  const capitalizedUnitLabel = t(
    `hire_unit_type_capitalized.${values?.hire_unit_type ?? HireUnitType.Day}`,
  ) as string;

  return { unitLabel, capitalizedUnitLabel };
}
